import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Tabs } from 'antd';
import { Button } from '@cropler/ui-kit';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import relativeTime from 'dayjs/plugin/relativeTime';

import styles from '../../styles/dashboard.module.scss';
import stylesTypography from '../../styles/typography.module.scss';

import {
  BreakpointDesktopUp,
  BreakpointMobile,
  BreakpointMobileUp,
  PathnamesMap,
  TabBarSensorStyle,
  TabBarSensorStyleMobile,
} from '../../constants/ui';
import { MapLat, MapLng } from '../../constants/keys';
import { Loading, TabLabel } from '../index.jsx';
import { Photo } from '../Photo/index.jsx';
import { EmptyViewData } from '../EmptyView/index.jsx';
import Panel from '../Panel/index.jsx';
import { useViewport, useLocalStorage } from '../../hooks';
import { useGetSensorsDatesQuery, useGetSensorsSummaryQuery } from '../../store/api/sensors';
import { useGetFieldsQuery } from '../../store/api/fields';
import { selectDate, selectMainSensor, selectSeason } from '../../store/slices/sensors';
import { Timeline } from '../Timeline/index.jsx';
import { SensorSidebar } from './index.jsx';
import { getSensorCoordinates } from '../../utils/sensor';
import { DevicesInfo } from '../Devices/index.jsx';
import { Charts } from '../Charts/index.jsx';
import { SidebarItemInner } from '../Sidebar/index.jsx';
import { Icon as CroplerIcon } from '@cropler/ui-kit';

dayjs.extend(relativeTime);
dayjs.extend(utc);

const Sensor = ({ t, type }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { widthViewport } = useViewport();

  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchParamsDate] = useState(searchParams.get('date'));

  const [, setMapLng] = useLocalStorage(MapLng, '');
  const [, setMapLat] = useLocalStorage(MapLat, '');

  const [isSelectedDateUpdated, setIsSelectedDateUpdated] = useState(false);

  //About device panel
  const [activeInfoPanel, setActiveInfoPanel] = useState(false);
  const [crop, setCrop] = useState('');
  const [field, setField] = useState('');
  const [seasons, setSeasons] = useState([]);
  const [sensor, setSensor] = useState(null);
  const [listFull, setListFull] = useState(null);

  const [showSoilData, setShowSoilData] = useState(false);

  const { selectedSensor, selectedDate } = useSelector((state) => state.sensors);

  const {
    data: dates = [],
    isFetching: isDatesLoading,
    isSuccess: isDatesSuccess,
    isError: isDatesError,
  } = useGetSensorsDatesQuery(
    {
      location_id: selectedSensor,
    },
    { skip: !selectedSensor, refetchOnMountOrArgChange: true },
  );

  const lastDate = dates[dates.length - 1];

  const {
    data: sensorFullData = [],
    isSuccess: isSensorSuccess,
    isFetching: isSensorLoading,
  } = useGetSensorsSummaryQuery(
    { location_id: selectedSensor, ordering: 'date' },
    { skip: !selectedSensor, refetchOnMountOrArgChange: true },
  );

  const { data: fields, isSuccess: isFieldsSuccess } = useGetFieldsQuery();

  const onBackToDashboard = () => {
    navigate('/sensors');
  };

  const onOpenAboutSensor = () => {
    setActiveInfoPanel(true);
  };

  const onChangeTab = (key) => {
    let pathname;
    switch (key) {
      case 'charts':
        pathname = PathnamesMap.charts;
        break;
      case 'vegetation':
        pathname = PathnamesMap.vegetation;
        break;
      case 'photo':
        pathname = PathnamesMap.photo;
        break;
      case 'charts-soil':
        pathname = PathnamesMap.soilSensorCharts;
        break;
      default:
        pathname = PathnamesMap.timeline;
    }
    navigate({
      pathname: `/${pathname}/${id}`,
      search: `?date=${selectedDate}`,
    }, { replace: true });
  };

  useEffect(() => {
    if (isDatesError) {
      navigate('/sensors');
    }
  }, [isDatesError]);

  // Set selectedSensor ID if not selected
  useEffect(() => {
    if (!id || selectedSensor === id) return;
    dispatch(selectMainSensor({ id }));
  }, [id, selectedSensor]);

  // Set selectedDate
  useEffect(() => {
    if (!isDatesSuccess || isSelectedDateUpdated || !dates.length) return;

    let date = selectedDate || searchParamsDate;

    // Select the last date if date does not exist
    if (!dates.find((d) => d === date)) {
      date = lastDate;
    }

    if (!selectedDate) {
      dispatch(selectDate(date));
    }

    if (!searchParamsDate) {
      searchParams.set('date', date);
    }

    setSearchParams(searchParams, { replace: true });
    setIsSelectedDateUpdated(true);
  }, [isDatesSuccess, isSelectedDateUpdated, dates]);

  // Get sensor data
  useEffect(() => {
    if (isSensorLoading || !isSensorSuccess || !selectedSensor || id !== selectedSensor || !isSelectedDateUpdated)
      return;
    // Show/hide soil data charts
    let isSoilSensorHasData = false;
    sensorFullData.map((item) => {
      if (!isSoilSensorHasData && item.soilMoisture !== null && item.soilMoisture.length > 0) {
        isSoilSensorHasData = true;
      }
    });
    setShowSoilData(isSoilSensorHasData);

    setListFull(sensorFullData);
  }, [isSensorLoading, isSensorSuccess, selectedSensor, isSelectedDateUpdated]);

  // Get sensor, seasons
  useEffect(() => {
    if (
      isDatesLoading ||
      isSensorLoading ||
      !isSensorSuccess ||
      !isFieldsSuccess ||
      !sensorFullData ||
      sensorFullData.length === 0 ||
      !selectedDate
    ) {
      return;
    }

    // Current Sensor
    const currentSensor = sensorFullData.filter((d) => d.date === selectedDate)[0];
    setSensor(currentSensor);

    // Get seasons list
    // Get/save current season
    const seasonsData = [];
    let season = '';
    fields.map((obj) => {
      if (obj.seasons && obj.seasons.length) {
        obj.seasons.map((item) => {
          seasonsData.push({
            ...item,
            field: obj.name,
          });

          setField(obj.name ? obj.name : '');

          if (
            !season &&
            currentSensor &&
            currentSensor.location &&
            currentSensor.location.field_season_id === item.id
          ) {
            season = {
              ...item,
              field: obj.name,
            };

            dispatch(selectSeason(obj.id));
            setCrop(season ? season.crop_id : '');
          }
        });
      }
    });
    setSeasons(seasonsData);

    // Save coordinates for a map
    if (currentSensor) {
      const sensorCoordinates = getSensorCoordinates({
        gpsPoint: currentSensor.location.gps_point,
        installPoint: currentSensor.location.install_point,
      });
      if (sensorCoordinates.length) {
        setMapLng(sensorCoordinates[0].toString());
        setMapLat(sensorCoordinates[1].toString());
      }
    }
  }, [
    isDatesLoading,
    isSensorLoading,
    isFieldsSuccess,
    isSensorSuccess,
    sensorFullData,
    selectedDate,
  ]);

  if ((!sensor || !dates) && (isSensorLoading || isDatesLoading)) {
    return <Loading />;
  }

  return (
    <>
      {dates ? (
        <div className="workspace">
          <SensorSidebar t={t} crop={crop || 'unbound'} seasons={seasons} />

          <div className={`workspace-body workspace-body--sensor sensor-component`}>
            {/* HEADER for mobile/tablet */}
            {widthViewport < BreakpointDesktopUp && (
              <div className={styles.sensorHeader}>
                {widthViewport < BreakpointMobileUp && (
                  <Button
                    type="text"
                    icon={<CroplerIcon name="prev" />}
                    className={styles.backButton}
                    onClick={onBackToDashboard}
                  ></Button>
                )}
                {sensor && (
                  <div className={styles.deviceInfo}>
                    <SidebarItemInner title={`${sensor.location.name || t('locations.noLocations')}, ${field}`} />
                  </div>
                )}
              </div>
            )}

            {sensor && (
              <Button
                className={`${stylesTypography.textButtonWithIcon} ${styles.sensorAboutButton}`}
                type={'text'}
                onClick={() => onOpenAboutSensor()}
              >
                <CroplerIcon name="info" />
                <span>{t('actions.about')}</span>
              </Button>
            )}

            <div className={styles.sensorTabsWrapper}>
              <Tabs
                rootClassName="sensor-tabs"
                defaultActiveKey={type}
                size="small"
                style={{ flexGrow: 1 }}
                tabBarStyle={widthViewport > BreakpointMobile ? TabBarSensorStyle : TabBarSensorStyleMobile}
                items={[
                  {
                    key: 'timeline',
                    label: <TabLabel label={t('sensor.tabs.timeline')} />,
                    children: (
                      <Timeline
                        sensorFullData={listFull}
                        sensor={sensor}
                        dates={dates}
                        crop={crop}
                        showSoilData={showSoilData}
                        isVegetation={false}
                        t={t}
                      />
                    ),
                  },
                  {
                    key: 'vegetation',
                    label: <TabLabel label={t('sensor.tabs.vegetation')} />,
                    children: (
                      <Timeline
                        sensorFullData={listFull}
                        sensor={sensor}
                        dates={dates}
                        crop={crop}
                        showSoilData={showSoilData}
                        isVegetation={true}
                        t={t}
                      />
                    ),
                  },
                  {
                    key: 'charts',
                    label: <TabLabel label={t('sensor.tabs.charts')} />,
                    children: <Charts t={t} showSoilData={showSoilData} sensorFullData={listFull} />,
                  },
                  {
                    key: 'photo',
                    label: <TabLabel label={t('sensor.tabs.photo')} />,
                    children: <Photo crop={crop} t={t} />,
                  },
                ]}
                onTabClick={onChangeTab}
              />
            </div>
          </div>
        </div>
      ) : (
        <EmptyViewData />
      )}

      {/* About / Device Information */}
      {activeInfoPanel && sensor && (
        <Panel
          title={t('sensor.info')}
          subtitle={sensor.location?.sensor?.name}
          setActivePanel={() => setActiveInfoPanel(false)}
          isNoMainMenu={true}
        >
          <DevicesInfo id={sensor.location?.sensor?.id} gps={sensor.location?.sensor.gps_point?.coordinates} t={t} />
        </Panel>
      )}
    </>
  );
};

export default Sensor;
